import React, { useState } from 'react';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { int, money, pct } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable } from '../components/ui';

// Customer acquisition: customers whose FIRST-EVER order lands in the selected
// range — how many, at which store, and what first-timers actually order.
// Modern port of the classic "New Customers" detail report (global filters +
// a local Online/POS source toggle). Only orders with a customer_id count —
// POS walk-ins (~27%) have none, so treat these as directional, not absolute.

const SOURCES = [
  { id: 'All', label: 'All orders' },
  { id: 'Online', label: 'Online only' },
  { id: 'POS', label: 'POS only' },
];

export default function NewCustomersPage() {
  const { reportParams, startDate, endDate, locations } = useFilters();
  const [orderSource, setOrderSource] = useState('All');

  const params = {
    startDate: reportParams.startDate,
    endDate: reportParams.endDate,
    // These endpoints filter on `selectedLocations` (legacy name) — pass the
    // global picker's selection through under that key.
    selectedLocations: reportParams.locations,
    orderSource,
  };

  const byLocation = useReport('new-customers', params);
  const topItems = useReport('top-items-new-customers', params);

  // Most food_order rows carry no location_name, so the endpoint groups by
  // location_id — resolve display names from the standard locations list,
  // falling back to whatever name the data had, then the raw id.
  const locName = new Map(locations.map(l => [Number(l.ID), l.NAME]));
  const rows = (byLocation.data || []).map((r, i) => ({
    locationName: locName.get(Number(r.locationId)) || r.locationName || `#${r.locationId}`,
    newCustomers: Number(r.newCustomers) || 0,
    __key: i,
  }));
  const total = rows.reduce((s, r) => s + r.newCustomers, 0);
  const maxLoc = rows.reduce((m, r) => Math.max(m, r.newCustomers), 0);

  const days = Math.max(1, Math.round(
    (new Date(endDate) - new Date(startDate)) / 864e5) + 1);

  const itemRows = (topItems.data || []).map((r, i) => ({
    rank: i + 1,
    itemName: r.itemName || 'Unknown',
    totalQuantity: Number(r.totalQuantity) || 0,
    revenueCents: Math.round(Number(r.totalRevenue || 0) * 100),
    __key: i,
  }));

  const sourceToggle = (
    <div className="chip-row">
      {SOURCES.map(s => (
        <button
          key={s.id}
          className={`chip ${orderSource === s.id ? 'active' : ''}`}
          onClick={() => setOrderSource(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  );

  const loading = byLocation.loading || topItems.loading;
  const error = byLocation.error || topItems.error;

  return (
    <div className="grid" style={{ gap: 14 }}>
      {error ? <ErrorBox message={error} /> :
        loading ? <Spinner label="Finding first-time customers…" /> : (
          <>
            <div className="grid kpi-grid">
              <KpiCard
                label="New Customers"
                value={int(total)}
                sub="first-ever order in this period"
              />
              <KpiCard
                label="Per Day"
                value={int(Math.round(total / days))}
                sub={`avg across ${days} day${days > 1 ? 's' : ''}`}
              />
              <KpiCard
                label="Top First Order"
                value={itemRows[0]?.itemName || '—'}
                sub={itemRows[0] ? `${int(itemRows[0].totalQuantity)} sold to new customers` : 'no data'}
              />
            </div>

            <Card
              tour="nc-bylocation"
              title="New Customers by Location"
              sub="where first-timers showed up · walk-ins without a customer profile aren't counted"
              actions={sourceToggle}
            >
              {rows.length === 0 ? <Empty>No new customers in this period.</Empty> : (
                <DataTable
                  csvName="new-customers-by-location.csv"
                  defaultSort={{ key: 'newCustomers', dir: 'desc' }}
                  columns={[
                    { key: 'locationName', label: 'Location' },
                    { key: 'newCustomers', label: 'New Customers', num: true, render: r => int(r.newCustomers), footer: rs => int(rs.reduce((s, r) => s + r.newCustomers, 0)) },
                    {
                      key: 'share', label: 'Share', num: true, sortValue: r => r.newCustomers,
                      render: r => (
                        <>
                          <span className="bar" style={{ width: `${maxLoc ? (r.newCustomers / maxLoc) * 60 : 0}px`, marginRight: 8 }} />
                          {pct(total ? r.newCustomers / total : 0)}
                        </>
                      ),
                      csv: r => (total ? (r.newCustomers / total * 100).toFixed(1) : '0'),
                      footer: () => '100%',
                    },
                  ]}
                  rows={rows}
                  footer
                />
              )}
            </Card>

            <Card
              tour="nc-items"
              title="What New Customers Order First"
              sub="top items on orders placed by first-time customers in this period"
            >
              {itemRows.length === 0 ? <Empty>No item data for this period.</Empty> : (
                <DataTable
                  csvName="new-customer-top-items.csv"
                  defaultSort={{ key: 'totalQuantity', dir: 'desc' }}
                  columns={[
                    { key: 'rank', label: '#', num: true },
                    { key: 'itemName', label: 'Item' },
                    { key: 'totalQuantity', label: 'Qty', num: true, render: r => int(r.totalQuantity) },
                    { key: 'revenueCents', label: 'Revenue', num: true, render: r => money(r.revenueCents), csv: r => (r.revenueCents / 100).toFixed(2) },
                  ]}
                  rows={itemRows}
                />
              )}
            </Card>
          </>
        )}
    </div>
  );
}
